import {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { useLocation } from "react-router-dom/cjs/react-router-dom.min";

const VisitContext = createContext(null);

export function VisitProvider({ children }) {
  const key = "visit:page";
  const location = useLocation();
  const isUpdatingRef = useRef(false);

  const setModuleVisits = useCallback(
    ({
      teacherId,
      module,
      moduleId,
      subject,
      chapter,
      topic,
      grade,
      section,
    }) => {
      const visitedModules = window.localStorage.getItem(key);
      const time = Date.now();

      let data = {};
      if (visitedModules) {
        data = JSON.parse(visitedModules);
      }

      window.localStorage.setItem(
        key,
        JSON.stringify({
          ...data,
          [moduleId]: {
            teacherId,
            moduleId,
            module,
            subject,
            chapter,
            topic,
            grade,
            section,
            visitedOn: data[moduleId]?.visitedOn || time, // preserve original visitedOn
            updatedOn: time,
          },
        }),
      );
    },
    [],
  );

  const updateVisits = useCallback(async (visits) => {
    for (let visitId of Object.keys(visits)) {
      const {
        teacherId,
        subject,
        chapter,
        topic,
        module,
        grade,
        section,
        visitedOn,
        updatedOn,
      } = visits[visitId];

      const duration = updatedOn - visitedOn;

      if (!module) {
        console.log("Skipped", visitId);
        continue;
      }

      try {
        await fetch(
          `${process.env.REACT_APP_ORIGIN}/api/v1/analytics/pagevisits`,
          {
            method: "POST",
            headers: {
              token: `${window.localStorage.getItem("authToken")}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              teacherId,
              subject,
              chapter,
              topic,
              module,
              grade,
              section,
              visitOn: visitedOn,
              duration,
            }),
          },
        );

        delete visits[visitId];
        window.localStorage.setItem(key, JSON.stringify(visits));
      } catch (e) {
        console.error("Failed to update visit:", e.message);
      }
    }
  }, []);

  useEffect(() => {
    if (!window.localStorage.getItem("authToken")) return;
    const excludedEndings = [
      "login",
      "watch",
      "show",
      "view",
      "doc",
      "watchSWF",
      "listen",
      "activity",
      "media",
    ];

    const pathname = location.pathname.toLowerCase();

    const shouldExclude = excludedEndings.some((ending) =>
      pathname.endsWith(ending.toLowerCase()),
    );

    if (shouldExclude) return;

    // 🚫 prevent parallel execution
    if (isUpdatingRef.current) return;
    isUpdatingRef.current = true;

    const rawVisits = window.localStorage.getItem(key);
    if (!rawVisits) {
      isUpdatingRef.current = false;
      return;
    }

    console.log("144 Updating page", isUpdatingRef.current);

    const visits = JSON.parse(rawVisits);

    updateVisits(visits).finally(() => {
      isUpdatingRef.current = false;
    });
  }, [location.pathname]);

  return (
    <VisitContext.Provider value={{ setModuleVisits, updateVisits }}>
      {children}
    </VisitContext.Provider>
  );
}

export function useVisit() {
  const context = useContext(VisitContext);
  if (!context) {
    throw new Error("useVisit must be used within a VisitProvider");
  }
  return context;
}
